// src/ui/screens/signin.ts
// AUTH: EMAIL + PASSWORD, A PASTED TOKEN, AND A QR JOIN — on the one Account screen.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHY EMAIL + PASSWORD, AND NOT A MAGIC LINK
// ══════════════════════════════════════════════════════════════════════════════════════════════
// A magic link looks kinder for a coach on a phone at a ground, and for a normal website it would
// be. For an INSTALLED PWA it is close to unusable, for reasons that are about the platform rather
// than about taste:
//
//   1. THE LINK OPENS IN THE BROWSER, NOT IN THIS APP. On iOS a home-screen PWA runs in its own
//      WebView with its OWN storage partition. Tapping a link in Mail opens Safari, so the tokens
//      land in Safari's IndexedDB — and this app, which is where the coach was standing, still shows
//      "no token". The failure is silent and looks exactly like the link not working.
//   2. IT NEEDS AN INBOX AT THE GROUND. Email arrives over the same thin signal, on a delay that
//      nobody can see or hurry, and the delay is worst when the network is worst — which is when
//      somebody is standing on a pitch waiting to film.
//   3. IT NEEDS SMTP CONFIGURED. Supabase's built-in mailer is rate-limited to a handful of messages
//      per hour and is explicitly not for production. Onboarding a squad would hit that wall on the
//      first afternoon; a real SMTP provider is a separate procurement.
//
// A password is typed once, is stored by every phone's password manager, and works in the installed
// app with no inbox and no redirect. So: password. When someone asks for magic link later, the place
// to add it is `supabase.ts` (`POST /auth/v1/otp`) and it is a small change — but adding it TODAY
// would mean shipping two sign-in paths, only one of which anybody could have tested at a ground.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE TOKEN ARRIVES IN THE URL *FRAGMENT*, NEVER THE QUERY STRING
// ══════════════════════════════════════════════════════════════════════════════════════════════
// A fragment (`#token=…`) is not sent to any server, is not written into server access logs, and is
// not forwarded in a `Referer` header. A query string (`?token=…`) is all three. Since the thing
// being carried is a credential, the difference is the whole design of the join link. The fragment
// is also stripped from the address bar the moment it is consumed, so the credential does not sit in
// browser history.

import { el, mount, section } from '../dom';
import { apiBaseLabel, setApiBase, apiBase } from '../../config';
import {
  canRefresh,
  clearSession,
  loadSession,
  looksExpired,
  missingSupabaseConfig,
  saveSession,
  signInWithSupabase,
  supabaseConfigured,
  type Session,
} from '../../auth/session';
import { deviceId } from '../../device/ids';

export interface SignInHandlers {
  onSignedIn: () => void;
}

/**
 * Consume a token handed over by a QR / join link.
 *
 * Called once at boot, BEFORE any screen renders. Returns true when a token was taken.
 */
export async function consumeJoinFragment(): Promise<boolean> {
  if (typeof location === 'undefined' || !location.hash) return false;
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  const token = params.get('token');
  const api = params.get('api');
  if (api) setApiBase(api);
  if (!token) return false;
  await saveSession(token, 'join_link');
  // Strip the credential out of the address bar and out of history.
  history.replaceState(null, '', location.pathname + location.search);
  return true;
}

/**
 * The password form.
 *
 * Rendered ONLY when this build has a Supabase URL and anon key. An unconfigured build shows the
 * variable names instead — the same discipline the rest of the app applies to a DARK backend, and
 * for the same reason: a form that cannot possibly submit is worse than no form, because someone
 * will type a real password into it and conclude their password is wrong.
 */
function passwordSection(h: SignInHandlers): HTMLElement {
  const missing = missingSupabaseConfig();
  if (!supabaseConfigured()) {
    return section('Sign in',
      el('p', { class: 'warn' },
        'This build cannot sign in with a password: it was built without '
        + `${missing.join(' and ')}.`),
      el('p', { class: 'note' },
        'Those are BUILD-TIME variables — setting them requires a rebuild, not a reload. They are '
        + 'the Supabase project URL and the anon (public) key, from the Supabase dashboard under '
        + 'Project Settings → API. The anon key is designed to be published in a browser bundle; '
        + 'the service_role key is the secret one and must never be given to this app.'),
      el('p', { class: 'note' }, 'Until then, paste an access token below.'),
    );
  }

  const email = el('input', {
    class: 'input',
    type: 'email',
    id: 'signin-email',
    name: 'email',
    autocomplete: 'username',
    autocapitalize: 'off',
    autocorrect: 'off',
    spellcheck: false,
    inputmode: 'email',
    placeholder: 'coach@example.com',
  });

  const password = el('input', {
    class: 'input',
    type: 'password',
    id: 'signin-password',
    name: 'password',
    // `current-password`, so the phone's password manager offers to fill and to save. This is the
    // whole reason a password is bearable on a phone at all.
    autocomplete: 'current-password',
    placeholder: 'Password',
  });

  const status = el('p', { class: 'note' });
  const button = el('button', { class: 'btn btn-primary', type: 'submit' }, 'Sign in');

  const submit = async (): Promise<void> => {
    button.disabled = true;
    status.className = 'note';
    status.textContent = 'Signing in…';
    // The password is read here and passed straight to the grant. It is never stored, never put on
    // the job queue, and never written to IndexedDB — only the tokens it buys are.
    const r = await signInWithSupabase(email.value, password.value);
    button.disabled = false;
    if (r.ok) {
      password.value = '';
      status.className = 'ok';
      status.textContent = r.session.email
        ? `Signed in as ${r.session.email}. This session renews itself.`
        : 'Signed in. This session renews itself.';
      h.onSignedIn();
      return;
    }
    status.className = 'warn';
    // The composed sentence from supabase.ts, which says what to DO. Never the raw GoTrue string,
    // and never anything derived from what was typed.
    status.textContent = r.message;
  };

  const form = el('form', {
    class: 'form',
    onsubmit: (e: Event) => { e.preventDefault(); void submit(); },
  },
    el('label', { class: 'label', for: 'signin-email' }, 'Email'),
    email,
    el('label', { class: 'label', for: 'signin-password' }, 'Password'),
    password,
    el('div', { class: 'row' }, button),
    status,
  );

  return section('Sign in',
    el('p', { class: 'note' },
      'Your DCS Sports account, the same one the dashboard uses. Signing in here gets a token that '
      + 'renews itself, so a two-hour session will not sign you out part-way through an upload.'),
    form,
  );
}

export async function signInScreen(root: HTMLElement, h: SignInHandlers): Promise<void> {
  const session: Session | undefined = await loadSession();
  const device = await deviceId();

  const tokenInput = el('textarea', {
    class: 'input mono',
    rows: 4,
    placeholder: 'Paste the Supabase access token (JWT) here',
    autocapitalize: 'off',
    autocorrect: 'off',
    spellcheck: false,
  });

  const baseInput = el('input', {
    class: 'input mono',
    type: 'text',
    value: apiBase(),
    placeholder: 'https://dcs-sports-backend-production.up.railway.app  (blank = same origin / dev proxy)',
  });

  const status = el('p', { class: 'note' });

  const save = async (): Promise<void> => {
    const raw = tokenInput.value.trim();
    if (!raw) { status.textContent = 'Nothing pasted — no token was stored.'; return; }
    try {
      const s = await saveSession(raw);
      status.textContent = s.subject
        ? `Stored. The token names subject ${s.subject}. The server will decide what it grants.`
        : 'Stored. The token could not be decoded locally, which is fine — the server verifies it.';
      h.onSignedIn();
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : String(e);
    }
  };

  mount(root,
    passwordSection(h),

    session && section('This session',
      el('p', {}, session.email ? `Signed in as ${session.email}.` : 'A token is stored.'),
      el('p', {}, session.subject ? `Subject: ${session.subject}` : 'Subject: not decodable locally'),
      el('p', {},
        session.expiresAt
          ? `Expires: ${new Date(session.expiresAt).toLocaleString()}`
          : 'Carries no readable expiry.'),
      // The one fact that decides whether this session survives the next hour. Said plainly, up
      // front, rather than discovered when an upload 401s in the middle of a net session.
      el('p', { class: canRefresh(session) ? 'ok' : 'warn' },
        canRefresh(session)
          ? 'This session renews itself automatically. A long filming session will not sign you out.'
          : 'This session CANNOT renew itself — it came from a pasted token or a join link. When it '
            + 'expires you will be signed out and will need to sign in with an email and password, '
            + 'or paste a new token.'),
      looksExpired(session) && !canRefresh(session) && el('p', { class: 'warn' },
        'This token is past its own expiry. The app will still send it — the server is the authority '
        + 'on that, not this device’s clock — and you should expect a 401.'),
      el('div', { class: 'row' },
        el('button', {
          class: 'btn',
          onclick: () => void clearSession().then(() => location.reload()),
        }, 'Sign out'),
      ),
    ),

    section('Backend',
      el('p', { class: 'note' }, `This app is pointed at: ${apiBaseLabel()}`),
      el('label', { class: 'label' }, 'API base URL'),
      baseInput,
      el('button', {
        class: 'btn',
        onclick: () => { setApiBase(baseInput.value); location.reload(); },
      }, 'Use this backend'),
      el('p', { class: 'note' },
        'Leave blank during `npm run dev` — Vite proxies /v1 and /health to the real backend, which '
        + 'keeps the call same-origin. A deployed build must use an absolute URL, and that origin '
        + 'must be listed in the backend’s ALLOWED_ORIGINS or the browser will block every call.'),
    ),

    section('Paste an access token',
      el('p', { class: 'note' },
        'For a tester or the Founder: a JWT from the Supabase dashboard, or from a curl against '
        + 'GoTrue. It works exactly like a signed-in session, with one difference — there is no '
        + 'refresh token, so it dies at its expiry instead of renewing.'),
      el('label', { class: 'label' }, 'Bearer token'),
      tokenInput,
      el('div', { class: 'row' },
        el('button', { class: 'btn', onclick: () => void save() }, 'Store token'),
      ),
      status,
    ),

    section('This device',
      el('p', { class: 'note' },
        'The offline queue keys every session to this device id. It is generated here, stored in '
        + 'IndexedDB, and never regenerated — that is what makes a retried session the same '
        + 'session instead of a second one.'),
      el('p', { class: 'mono small' }, device),
    ),

    section('Joining by QR',
      el('p', { class: 'note' },
        'A join link carries its token in the URL fragment (#token=…), which is never sent to a '
        + 'server, never logged, and never put in a Referer header. It is consumed and wiped from the '
        + 'address bar on arrival.'),
      el('p', { class: 'note' },
        'Scanning is done with the phone’s own camera app, the same decision as capture: the '
        + 'native scanner is reliable everywhere, and this build does not open a browser camera.'),
    ),
  );
}
